/**
 * Custom hook for navigation event handlers
 */

import { useEffect } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useHistory } from 'react-router-dom';
import _ from 'lodash';

import { type RootState } from '../../../services/serviceTypes';
import type { StepKey, StepNavigationProps } from '../data-layer/types';
import {
  addVisitedStep,
  openSaveDraftModal,
  resetRemoveAccessEntitlementIndex,
  setActiveStep,
  setAddAccessInputField,
  setFormStatus,
  setSavedStepData,
} from '../data-store/copilotSlice';
import showStepForm from '../utils/stepFormDisplayUtils';
import { handleKeyboardNavigation } from '../utils/stepHelpers';

interface UseNavigationHandlersProps {
  scrollToStep: Pick<StepNavigationProps, 'scrollToStep'>['scrollToStep'];
  visitedSteps: string[];
}

const useNavigationHandlers = ({
  scrollToStep,
  visitedSteps,
}: UseNavigationHandlersProps) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const store = useStore<RootState>();

  // Single selector for all needed data
  const {
    activeStep,
    stepFormData,
    savedStepData,
    isEditMode,
    saveDraftModalOpen,
    skippedSteps,
  } = useSelector((state: RootState) => {
    const { copilot } = state;
    const currentActiveStep = copilot.navigation.activeStep;

    return {
      activeStep: currentActiveStep,
      stepFormData: copilot.stepFormData[currentActiveStep] || {},
      savedStepData: copilot.savedStepData[currentActiveStep] || {},
      isEditMode: copilot.ui.mode === 'edit',
      saveDraftModalOpen: copilot.saveDraftModal.isOpen,
      skippedSteps: copilot.navigation.skippedSteps,
    };
  });

  // useEffect to handle form status when save draft modal closes
  useEffect(() => {
    if (!saveDraftModalOpen && activeStep) {
      // When modal closes, ensure the active step is in expandable view
      dispatch(
        setFormStatus({ stepName: activeStep, formStatus: 'expandableview' })
      );
    }
  }, [saveDraftModalOpen, activeStep, dispatch]);

  // Check for unsaved changes with special handling for UpdateAccount prefilled data
  const hasUnsavedChanges = (() => {
    // Special case for UpdateAccount: if savedStepData is empty but stepFormData has content,
    // it's likely prefilled data, not user changes
    if (
      activeStep === 'UpdateAccount' &&
      Object.keys(savedStepData).length === 0 &&
      Object.keys(stepFormData).length > 0
    ) {
      return false;
    }

    if (activeStep.includes('Access_')) {
      // For Access steps, only compare mappings (the main user-editable field)
      return !(
        JSON.stringify(stepFormData.mappings) ===
          JSON.stringify(savedStepData.mappings) ||
        Object.keys(savedStepData).length === 0
      );
    }

    // Only check current active step for unsaved changes
    return !(
      _.isEqual(stepFormData, savedStepData) ||
      Object.keys(savedStepData).length === 0
    );
  })();

  const handleBackClick = () => {
    history.push('/integrations');
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    handleKeyboardNavigation(event, handleBackClick);
  };

  const handleStepClick = (stepId: string) => {
    if (visitedSteps.includes(stepId)) {
      // Check for unsaved changes before navigation
      if (hasUnsavedChanges) {
        // Show modal instead of direct navigation
        dispatch(
          openSaveDraftModal({
            stepName: activeStep,
            targetStep: stepId,
          })
        );

        return;
      }

      // No unsaved changes, proceed with normal navigation
      scrollToStep(stepId as StepKey);
      dispatch(setActiveStep(stepId));

      if (stepId === 'AddAccess' && isEditMode) {
        dispatch(setAddAccessInputField(false));
      }

      // Reset removeAccessEntitlementIndex when navigating to RemoveAccess
      if (stepId === 'RemoveAccess') {
        dispatch(resetRemoveAccessEntitlementIndex());

        if (skippedSteps.includes('AddAccess')) {
          return;
        }
      }

      // Show form for the clicked step (pass form data directly)
      const currentState = store.getState();

      showStepForm(
        dispatch,
        currentState.copilot.stepFormData,
        stepId,
        isEditMode,
        () => store.getState()
      );

      // In edit mode, always show forms in expanded mode (after showStepForm)
      dispatch(
        setFormStatus({ stepName: stepId, formStatus: 'expandableview' })
      );

      // Set saved step data after form initialization for Connection and General steps
      if (stepId === 'Connection' || stepId === 'General') {
        const state = store.getState();
        const currentFormData = state.copilot.stepFormData[stepId];

        dispatch(
          setSavedStepData({ stepName: stepId, formData: currentFormData })
        );
      }

      if (!visitedSteps.includes(stepId)) {
        dispatch(addVisitedStep(stepId));
      }
    }
  };

  return {
    handleBackClick,
    handleKeyDown,
    handleStepClick,
  };
};

export default useNavigationHandlers;
